import React from 'react';

export const GridBehavior = {
  id: 'Grid',
  label: 'Grid / Layout Group',
  icon: 'Grid',
  description: 'A unified layout group handling Grid, Vertical, and Horizontal arrangements.',
  isLayoutProvider: true,
  fields: [
    { name: 'layoutMode', label: 'Layout Mode', type: 'select', options: ['Grid', 'Vertical', 'Horizontal'], default: 'Grid' },

    {
      name: 'paddingGroup',
      label: 'Padding',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'paddingLeft', label: 'Left', type: 'number', default: 0 },
        { name: 'paddingRight', label: 'Right', type: 'number', default: 0 },
        { name: 'paddingTop', label: 'Top', type: 'number', default: 0 },
        { name: 'paddingBottom', label: 'Bottom', type: 'number', default: 0 },
      ]
    },

    {
      name: 'spacingGroup',
      label: 'Spacing',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'spacingX', label: 'X', type: 'number', default: 0 },
        { name: 'spacingY', label: 'Y', type: 'number', default: 0 },
      ]
    },

    {
      name: 'alignmentGroup',
      label: 'Alignment',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'childAlignment', label: 'Child Alignment', type: 'select', options: [
          'UpperLeft', 'UpperCenter', 'UpperRight',
          'MiddleLeft', 'MiddleCenter', 'MiddleRight',
          'LowerLeft', 'LowerCenter', 'LowerRight'
        ], default: 'UpperLeft' },
        { name: 'startCorner', label: 'Start Corner', type: 'select', options: ['Upper Left', 'Upper Right', 'Lower Left', 'Lower Right'], default: 'Upper Left' },
        { name: 'startAxis', label: 'Start Axis', type: 'select', options: ['Horizontal', 'Vertical'], default: 'Vertical' },
      ]
    },

    {
      name: 'cellSizeGroup',
      label: 'Cell Size',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'cellSizeX', label: 'X', type: 'number', default: 100 },
        { name: 'cellSizeY', label: 'Y', type: 'number', default: 100 },
      ]
    },

    {
      name: 'constraintGroup',
      label: 'Constraint',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'constraint', label: 'Type', type: 'select', options: ['Flexible', 'Fixed Column Count', 'Fixed Row Count'], default: 'Fixed Column Count' },
        { name: 'constraintCount', label: 'Count', type: 'number', default: 1 },
      ]
    },

    {
      name: 'layoutControlGroup',
      label: 'Layout Control',
      type: 'group',
      defaultOpen: false,
      fields: [
        { name: 'reverseArrangement', label: 'Reverse', type: 'boolean', default: false },
        { name: 'controlChildWidth', label: 'Control Child Width', type: 'boolean', default: false },
        { name: 'controlChildHeight', label: 'Control Child Height', type: 'boolean', default: false },
      ]
    },
  ],
  getStyle: (props) => {
    const mode = props.layoutMode || 'Grid';
    const paddingLeft = props.paddingLeft ?? 0;
    const paddingRight = props.paddingRight ?? 0;
    const paddingTop = props.paddingTop ?? 0;
    const paddingBottom = props.paddingBottom ?? 0;
    const spacingX = props.spacingX || 0;
    const spacingY = props.spacingY || 0;
    const alignment = props.childAlignment || 'UpperLeft';
    const reverse = props.reverseArrangement || false;

    // Common Base
    const style = {
      paddingLeft: `${paddingLeft}px`,
      paddingRight: `${paddingRight}px`,
      paddingTop: `${paddingTop}px`,
      paddingBottom: `${paddingBottom}px`,
      boxSizing: 'border-box',
    };

    if (mode === 'Grid') {
        const cellSizeX = props.cellSizeX || 100;
        const cellSizeY = props.cellSizeY || 100;
        const startAxis = props.startAxis || 'Vertical';
        const startCorner = props.startCorner || 'Upper Left';
        const constraint = props.constraint || 'Fixed Column Count';
        const count = props.constraintCount || 1;

        style.display = 'grid';
        style.gap = `${spacingY}px ${spacingX}px`;

        // Map Alignment - only used when cells don't fill the container
        let justifyItems = 'start';
        let alignItems = 'start';
        let justifyContent = 'start';
        let alignContent = 'start';
        if (alignment.includes('Center')) { justifyItems = 'center'; justifyContent = 'center'; }
        if (alignment.includes('Right')) { justifyItems = 'end'; justifyContent = 'end'; }
        if (alignment.includes('Middle')) { alignItems = 'center'; alignContent = 'center'; }
        if (alignment.includes('Lower')) { alignItems = 'end'; alignContent = 'end'; }

        style.justifyItems = justifyItems;
        style.alignItems = alignItems;
        style.justifyContent = justifyContent;
        style.alignContent = alignContent;

        // Determine direction based on Start Corner
        const reverseHorizontal = startCorner.includes('Right');
        const reverseVertical = startCorner.includes('Lower');

        // Grid Logic: Start Axis determines primary flow direction
        if (startAxis === 'Horizontal') {
            // Horizontal means fill row first, then move to next row
            style.gridAutoFlow = reverseVertical ? 'row dense' : 'row';
            style.gridAutoRows = `${cellSizeY}px`;

            if (constraint === 'Fixed Column Count') {
                style.gridTemplateColumns = `repeat(${count}, ${cellSizeX}px)`;
            } else if (constraint === 'Fixed Row Count') {
                // Fixed rows + Horizontal axis = fill rows, expand columns
                style.gridTemplateRows = `repeat(${count}, ${cellSizeY}px)`;
                style.gridAutoFlow = reverseHorizontal ? 'column dense' : 'column';
                style.gridAutoColumns = `${cellSizeX}px`;
            } else {
                // Flexible: auto-fill columns
                style.gridTemplateColumns = `repeat(auto-fill, ${cellSizeX}px)`;
            }

            // Apply direction reversal
            if (reverseHorizontal) style.direction = 'rtl';
            if (reverseVertical) {
                style.gridAutoFlow = 'row-reverse';
            }
        } else {
            // Vertical means fill column first, then move to next column
            style.gridAutoFlow = reverseHorizontal ? 'column dense' : 'column';
            style.gridAutoColumns = `${cellSizeX}px`;

            if (constraint === 'Fixed Row Count') {
                style.gridTemplateRows = `repeat(${count}, ${cellSizeY}px)`;
            } else if (constraint === 'Fixed Column Count') {
                // Fixed columns + Vertical axis = fill columns, expand rows
                style.gridTemplateColumns = `repeat(${count}, ${cellSizeX}px)`;
                style.gridAutoFlow = reverseVertical ? 'row dense' : 'row';
                style.gridAutoRows = `${cellSizeY}px`;
            } else {
                // Flexible: auto-fill rows
                style.gridTemplateRows = `repeat(auto-fill, ${cellSizeY}px)`;
            }

            // Apply direction reversal
            if (reverseHorizontal) {
                style.gridAutoFlow = 'column-reverse';
            }
            if (reverseVertical) style.direction = 'rtl';
        }

    } else {
        // Vertical or Horizontal
        style.display = 'flex';
        // Use the relevant spacing property for the gap
        const gap = mode === 'Horizontal' ? spacingX : spacingY;
        style.gap = `${gap}px`;

        const isVert = mode === 'Vertical';
        style.flexDirection = isVert 
            ? (reverse ? 'column-reverse' : 'column') 
            : (reverse ? 'row-reverse' : 'row');

        // Alignment Mapping for Flex
        // Main Axis: Justify Content
        // Cross Axis: Align Items
        let mainAlign = 'flex-start';
        let crossAlign = 'flex-start';

        if (isVert) {
            // Main: Vertical (Upper/Middle/Lower)
            if (alignment.includes('Middle')) mainAlign = 'center';
            if (alignment.includes('Lower')) mainAlign = 'flex-end';
            // Cross: Horizontal (Left/Center/Right)
            if (alignment.includes('Center')) crossAlign = 'center';
            if (alignment.includes('Right')) crossAlign = 'flex-end';
        } else {
             // Main: Horizontal (Left/Center/Right)
             if (alignment.includes('Center')) mainAlign = 'center';
             if (alignment.includes('Right')) mainAlign = 'flex-end';
             // Cross: Vertical (Upper/Middle/Lower)
             if (alignment.includes('Middle')) crossAlign = 'center';
             if (alignment.includes('Lower')) crossAlign = 'flex-end';
        }

        style.justifyContent = mainAlign;
        style.alignItems = crossAlign;
    }

    return style;
  },
  getChildStyle: (props, childProps, childNode) => {
    const mode = props.layoutMode || 'Grid';
    const style = { position: 'relative' };

    if (mode === 'Grid') {
        const cellSizeX = props.cellSizeX || 100;
        const cellSizeY = props.cellSizeY || 100;

        // Grid Layout Group always sets fixed size for all children
        // It ignores minimum, preferred, and flexible size properties
        style.width = `${cellSizeX}px`;
        style.height = `${cellSizeY}px`;

        // Reset direction if parent applied it (for RTL handling)
        const startCorner = props.startCorner || 'Upper Left';
        const reverseHorizontal = startCorner.includes('Right');
        if (reverseHorizontal) {
            style.direction = 'ltr'; // Reset child direction
        }

    } else {
        const isVert = mode === 'Vertical';

        // Control Child Width
        if (props.controlChildWidth) {
            if (isVert) {
                // Vertical Stack: Control Width = Stretch to fill container width
                style.width = '100%';
                style.alignSelf = 'stretch';
            } else {
                // Horizontal Stack: Control Width = Fill remaining space in main axis (Grow)
                style.flexGrow = 1;
                style.flexBasis = '0px'; // Ensure growth is shared equally regardless of content
                style.width = 'auto'; 
            }
        } else {
            // NOT Controlled: Use object's own width
            // Do NOT set width here, letting the element's own style (from box props) take effect
            // However, ensure flex doesn't weirdly shrink it
            style.flexShrink = 0; 
            
            // Check for Stretch Anchors (Horizontal Stretch in Vertical Layout)
            if (isVert && childNode && childNode.anchors && childNode.anchors.minX !== childNode.anchors.maxX) {
                style.alignSelf = 'stretch';
                style.width = 'auto';
            }
        }

        // Control Child Height
        if (props.controlChildHeight) {
            if (isVert) {
                // Vertical Stack: Control Height = Fill remaining space in main axis (Grow)
                style.flexGrow = 1;
                style.flexBasis = '0px'; // Ensure growth is shared equally regardless of content
                style.height = 'auto';
            } else {
                // Horizontal Stack: Control Height = Stretch to fill container height
                style.height = '100%';
                style.alignSelf = 'stretch';
            }
        } else {
            // NOT Controlled: Use object's own height
            style.flexShrink = 0;

            // Check for Stretch Anchors (Vertical Stretch in Horizontal Layout)
            if (!isVert && childNode && childNode.anchors && childNode.anchors.minY !== childNode.anchors.maxY) {
                style.alignSelf = 'stretch';
                style.height = 'auto';
            }
        }
    }

    return style;
  }
};
